import React, { useState, useContext, useEffect, ReactNode } from 'react';
import { BackTop, Drawer, Divider, Avatar, Spin, message, Badge } from 'antd';
import { Switch, Route, NavLink, Redirect, Link, RouteProps } from 'react-router-dom';
import {
  MenuOutlined,
  UserOutlined,
  ReadOutlined,
  WechatOutlined,
  LockOutlined,
  CodeSandboxOutlined,
  LogoutOutlined,
  FileTextOutlined,
  TeamOutlined,
  QuestionCircleOutlined,
  SafetyOutlined,
  EditOutlined,
  HomeOutlined,
  MailOutlined,
  PayCircleOutlined,
} from '@ant-design/icons';
import gql from 'graphql-tag';
import { useQuery, useApolloClient } from '@apollo/client';

import { COMPANY_FULL_NAME, SHOW_INSURANCE_PAGE } from '../../../config';

import './index.css';
import Groups from '../Groups';
import Group from '../Group';
import Login from '../Login';
import Packages from '../Packages';
import Orders from '../Orders';
import AddOrEditPackage from '../AddOrEditPackage';
import Tutorial from '../Tutorial';
import AddGroup from '../AddGroup';
import { Insurance } from '../../../config';
import Contact from '../Contact';
import PlaceOrder from '../PlaceOrder';
import FAQ from '../FAQ';
import UpdateOrderAddress from '../UpdateOrderAddress';
import ChangePassword from '../ChangePassword';
import EditProfile from '../EditProfile';
import WarehouseAddress from '../WarehouseAddress';
import Notifications from '../Notifications';
import ShippingServices from '../ShippingServices';

import AppContext, { emptyUser } from '../../../components/AppContext';
import Error from '../../../components/Error';
import { AUTH_TOKEN } from '../../../utils/constants';
import { setErrorReportingUser } from '../../../utils/errorReporting';
import { AuthPayload, User } from '../../../utils/types';

export const USER_QUERY = gql`
  {
    user {
      id
      code
      email
      wechatId
      name
      country
      city
      state
      zipCode
      recipientName
      phone
      address
      operator {
        id
        name
        wechatId
        phone
      }
    }
  }
`;

const NOTIFICATIONS_QUERY = gql`
  {
    notifications {
      totalUnread
    }
  }
`;

function App() {
  const [drawerVisible, setDrawerVisible] = useState(false);

  const [notificationsTotalUnread, setNotificationsTotalUnread] = useState<number>(0);

  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem(AUTH_TOKEN));

  const [user, setUser] = useState<User | null>(null);

  const {
    loading: loadingUser,
    data: userData,
    error: loadUserError,
    refetch: refetchUserInfo,
  } = useQuery<{ user: User }, void>(USER_QUERY, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (userData?.user) {
      setUser(userData.user);
    }
  }, [userData]);

  useQuery(NOTIFICATIONS_QUERY, {
    variables: {},
    fetchPolicy: 'cache-and-network',
    skip: !isAuthenticated,
    onCompleted: data => setNotificationsTotalUnread(data.notifications.totalUnread),
  });

  const client = useApolloClient();

  const updateAuthInfo = (authPayload: AuthPayload = { user: null }) => {
    if (authPayload.user) {
      setUser(authPayload.user);
    }

    if (authPayload.token) {
      setIsAuthenticated(true);
      localStorage.setItem(AUTH_TOKEN, authPayload.token);
    }
  };

  setErrorReportingUser(user);

  const logout = () => {
    setDrawerVisible(false);
    message.success('已退出');
    setUser(null);
    setIsAuthenticated(false);
    localStorage.clear();
    client.clearStore();
  };

  return (
    <AppContext.Provider
      value={{
        user: user || emptyUser,
        updateAuthInfo,
        refetchUserInfo: async () => { await refetchUserInfo(); },
        isAuthenticated,
        loadingUser,
        loadUserError: !!loadUserError,
        logout,
        drawerVisible,
        setDrawerVisible,
        setNotificationsTotalUnread,
      }}
    >
      <div className="App">
        <header className="App-header">
          <div
            onClick={() => setDrawerVisible(true)}
            style={{ cursor: 'pointer', position: 'absolute', left: '1rem', top: '8px' }}
          >
            <MenuOutlined />
          </div>
          {/* <div className="App-title">{TitleSvg}</div> */}
          <div className="App-title">{COMPANY_FULL_NAME}</div>
          {isAuthenticated && (
            <div
              style={{
                cursor: 'pointer',
                position: 'absolute',
                right: '1rem',
                top: '8px',
              }}
            >
              <Link to="/notifications">
                <Badge dot count={notificationsTotalUnread}>
                  <MailOutlined style={{ fontSize: '1.1rem', color: 'white' }} />
                </Badge>
              </Link>
            </div>
          )}
        </header>

        <DrawerMenu />

        <div className="App-main">
          <Switch>
            <Route path="/tutorial">
              <Tutorial />
              {/* <Nav /> */}
            </Route>
            <Route path="/address">
              <WarehouseAddress />
              {/* <Nav /> */}
            </Route>
            <Route path="/faq">
              <FAQ />
              {/* <Nav /> */}
            </Route>
            <Route path="/price">
              <ShippingServices />
              {/* <Nav /> */}
            </Route>
            <Route path="/contraband">
              <Redirect to="/price" />
              {/* <Nav /> */}
            </Route>
            <Route path="/contact">
              <Contact />
              {/* <Nav /> */}
            </Route>
            {SHOW_INSURANCE_PAGE && <Route path="/insurance">
              <Insurance />
            </Route>}
            <Route path="/login" key="login">
              <Login />
            </Route>
            <Route path="/signup">
              <Login tab="signup" key="signup" />
            </Route>
            <PrivateRoute path="/groups/add">
              <AddGroup mode="add" key="add_group" />
            </PrivateRoute>
            <PrivateRoute path="/groups/:id/edit">
              <AddGroup mode="edit" key="edit_group" />
            </PrivateRoute>
            <Route path="/groups/:id">
              <Group />
              {/* <Nav /> */}
            </Route>
            <Route path="/groups">
              <Groups />
              {/* <Nav /> */}
            </Route>
            <PrivateRoute path="/notifications">
              <Notifications />
            </PrivateRoute>
            <PrivateRoute path="/orders/:id/update-address">
              <UpdateOrderAddress />
              {/* <Nav /> */}
            </PrivateRoute>
            <PrivateRoute path="/orders/create">
              <PlaceOrder />
            </PrivateRoute>
            <PrivateRoute path="/orders">
              <Orders />
              {/* <Nav /> */}
            </PrivateRoute>
            <PrivateRoute path="/packages/:id/edit">
              <AddOrEditPackage mode="edit" key="edit_package" />
            </PrivateRoute>
            <PrivateRoute path="/packages/add">
              <AddOrEditPackage mode="add" key="add_package" />
            </PrivateRoute>
            <PrivateRoute path="/packages">
              <Packages />
              {/* <Nav /> */}
            </PrivateRoute>
            <PrivateRoute path="/change-password">
              <ChangePassword />
            </PrivateRoute>
            <PrivateRoute path="/profile/edit">
              <EditProfile />
            </PrivateRoute>
            <PrivateRoute path="/">
              <Packages />
              {/* <Nav /> */}
            </PrivateRoute>
          </Switch>
        </div>

        <BackTop />
      </div>
    </AppContext.Provider>
  );
}

// A wrapper for <Route> that redirects to the login
// screen if you're not yet authenticated.
function PrivateRoute({ children, ...rest }: RouteProps & { children: ReactNode | ReactNode[] }) {
  const { isAuthenticated } = useContext(AppContext);
  return (
    <Route
      {...rest}
      render={({ location }) => {
        if (isAuthenticated) return children;
        return <Redirect to={`/login?from=${location.pathname}`} />;
      }}
    />
  );
}

// function Nav() {
//   return (
//     <div className="App-nav">
//       <NavLink exact activeClassName="App-nav-active" className="App-nav-item App-nav-item-groups" to="/">
//         <TeamOutlined />
//         <div className="App-nav-text">{NavGroupSvg}</div>
//       </NavLink>
//       <NavLink activeClassName="App-nav-active" className="App-nav-item App-nav-item-package" to="/packages">
//         <CodeSandboxOutlined />
//         <div className="App-nav-text">{NavPackageSvg}</div>
//       </NavLink>
//     </div>
//   );
// }

function DrawerMenu() {
  const {
    isAuthenticated,
    user,
    loadingUser,
    loadUserError,
    logout,
    drawerVisible,
    setDrawerVisible,
  } = useContext(AppContext);

  let userInfo = (
    <Spin spinning={loadingUser}>
      <div style={{ padding: '24px 24px 0 24px', textAlign: 'center' }}>
        {isAuthenticated ? (
          <div className="App-drawer-user">
            <div className="App-drawer-userAvatar">
              <Avatar shape="square" icon={<UserOutlined />} />
            </div>
            <div className="App-drawer-userInfo">
              <div style={{ color: '#EB6841', fontSize: '1.1rem' }}>
                {user.name || user.wechatName} &nbsp;
                <Link
                  to={{
                    pathname: `/profile/edit`,
                    state: { user },
                  }}
                  onClick={() => setDrawerVisible(false)}
                >
                  <EditOutlined key="edit" style={{ color: '#009795' }} />
                </Link>
              </div>
              <div>{user.email}</div>
              <div>
                会员号：
                <span style={{ color: '#EB6841', fontSize: '1.1rem', fontWeight: 'bold' }}>
                  {user.code}
                </span>
              </div>
              <div>微信号：{user.wechatId}</div>
              <div>操作员：{(user.operator || {}).name}</div>
            </div>
          </div>
        ) : (
          <Link to="/login" onClick={() => setDrawerVisible(false)}>
            登录 / 注册
          </Link>
        )}
      </div>
    </Spin>
  );

  if (loadUserError) {
    userInfo = <Error message="无法获取用户信息，请联系管理员" />;
  }

  return (
    <Drawer
      placement="left"
      closable={false}
      width="80vw"
      onClose={() => {
        setDrawerVisible(false);
      }}
      visible={drawerVisible}
    >
      {userInfo}

      <Divider />

      <div className="App-drawerMenu">
        <NavLink
          onClick={() => setDrawerVisible(false)}
          exact
          to="/groups"
          className="App-drawerMenuItem"
          activeClassName="App-drawerMenuItem-active"
        >
          <TeamOutlined />
          <span>拼邮团</span>
        </NavLink>

        <NavLink
          onClick={() => setDrawerVisible(false)}
          exact
          to="/"
          className="App-drawerMenuItem"
          activeClassName="App-drawerMenuItem-active"
        >
          <CodeSandboxOutlined />
          <span>我的包裹</span>
        </NavLink>

        <NavLink
          onClick={() => setDrawerVisible(false)}
          exact
          to="/orders"
          className="App-drawerMenuItem"
          activeClassName="App-drawerMenuItem-active"
        >
          <FileTextOutlined />
          <span>我的订单</span>
        </NavLink>
        <NavLink
          onClick={() => setDrawerVisible(false)}
          exact
          to="/notifications"
          className="App-drawerMenuItem"
          activeClassName="App-drawerMenuItem-active"
        >
          <MailOutlined />
          <span>系统通知</span>
        </NavLink>

        <NavLink
          onClick={() => setDrawerVisible(false)}
          exact
          to="/price"
          className="App-drawerMenuItem"
          activeClassName="App-drawerMenuItem-active"
        >
          <PayCircleOutlined />
          <span>线路渠道</span>
        </NavLink>

        <NavLink
          onClick={() => setDrawerVisible(false)}
          exact
          to="/address"
          className="App-drawerMenuItem"
          activeClassName="App-drawerMenuItem-active"
        >
          <HomeOutlined />
          <span>仓库地址</span>
        </NavLink>

        <NavLink
          onClick={() => setDrawerVisible(false)}
          exact
          to="/tutorial"
          className="App-drawerMenuItem"
          activeClassName="App-drawerMenuItem-active"
        >
          <ReadOutlined />
          <span>转运教程</span>
        </NavLink>

        <NavLink
          onClick={() => setDrawerVisible(false)}
          exact
          to="/faq"
          className="App-drawerMenuItem"
          activeClassName="App-drawerMenuItem-active"
        >
          <QuestionCircleOutlined />
          <span>常见问题</span>
        </NavLink>

        {SHOW_INSURANCE_PAGE && <NavLink
          onClick={() => setDrawerVisible(false)}
          exact
          to="/insurance"
          className="App-drawerMenuItem"
          activeClassName="App-drawerMenuItem-active"
        >
          <SafetyOutlined />
          <span>丢失保险</span>
        </NavLink>}

        <NavLink
          onClick={() => setDrawerVisible(false)}
          exact
          to="/contact"
          className="App-drawerMenuItem"
          activeClassName="App-drawerMenuItem-active"
        >
          <WechatOutlined />
          <span>联系我们</span>
        </NavLink>

        {isAuthenticated && (
          <NavLink
            onClick={() => setDrawerVisible(false)}
            exact
            to={{
              pathname: `/profile/edit`,
              state: { user },
            }}
            className="App-drawerMenuItem"
            activeClassName="App-drawerMenuItem-active"
          >
            <UserOutlined />
            <span>账户信息</span>
          </NavLink>
        )}

        {isAuthenticated && (
          <NavLink
            onClick={() => setDrawerVisible(false)}
            exact
            to="/change-password"
            className="App-drawerMenuItem"
            activeClassName="App-drawerMenuItem-active"
          >
            <LockOutlined />
            <span>修改密码</span>
          </NavLink>
        )}
      </div>

      <Divider />

      {isAuthenticated && (
        <div style={{ textAlign: 'center', cursor: 'pointer' }} onClick={logout}>
          退出 <LogoutOutlined />
        </div>
      )}
    </Drawer>
  );
}

export default App;
